import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { GroupAdd as GroupAddIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
  Rectangle,
} from 'recharts';
import { AdminService } from '../../services/adminService';

/**
 * Nouvelles familles par jour, empilées par l'étape la plus avancée atteinte.
 * Une famille = le parent rattaché, sinon le compte joueur seul : deux enfants
 * d'un même parent ne comptent qu'une fois, au jour où le premier a commencé.
 * La définition fait foi côté serveur (`admin.queries` → `getNouvellesFamillesParJour`).
 */

// Étapes ordonnées : une seule teinte, du clair (commencé) au foncé (identifié).
// Ordre d'empilement = du bas vers le haut ; la plus avancée sert de socle.
const ETAPES = [
  { cle: 'identifiees', libelle: 'Compte identifié', couleur: '#184f95' },
  { cle: 'terminees', libelle: 'Onboarding terminé', couleur: '#3987e5' },
  { cle: 'commencees', libelle: 'Onboarding commencé seulement', couleur: '#86b6ef' },
];

// Plateforme d'inscription (`users.signup_platform`) ; une valeur inconnue de cette
// liste s'affiche telle quelle.
const LIBELLES_PLATEFORME = {
  ios: 'iOS',
  android: 'Android',
  web_app: 'Webapp',
  web_marketing: 'Site',
  parent_portal: 'Portail parent',
  other: 'Autre',
  inconnue: 'Inconnue',
};
const libellePlateforme = (cle) => LIBELLES_PLATEFORME[cle] ?? cle;

/** Additionne, jour par jour, les plateformes retenues (`null` = toutes). */
function serieFiltree(serie, plateforme) {
  return serie.map(({ jour, plateformes }) => {
    const ligne = { jour, commencees: 0, terminees: 0, identifiees: 0 };
    for (const [cle, compte] of Object.entries(plateformes)) {
      if (plateforme && cle !== plateforme) continue;
      for (const e of ETAPES) ligne[e.cle] += compte[e.cle] || 0;
    }
    return ligne;
  });
}

// Un segment vide n'est pas dessiné : son liseré blanc hacherait l'axe du bas.
const Segment = (props) => (props.height > 0 ? <Rectangle {...props} /> : null);

// 'YYYY-MM-DD' lu à midi : un minuit UTC basculerait la veille selon le fuseau.
const formatJour = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '';
const formatJourLong = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '—';

function Infobulle({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const ligne = payload[0].payload;
  const total = ETAPES.reduce((s, e) => s + (ligne[e.cle] || 0), 0);
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{formatJourLong(label)}</Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>Nouvelles familles : <strong>{total}</strong></Typography>
      {ETAPES.map((e) => (
        <Box key={e.cle} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: e.couleur }} />
          <Typography variant="body2">{e.libelle} : <strong>{ligne[e.cle] || 0}</strong></Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function NouvellesFamillesSection({ segment = 'familles' }) {
  const [jours, setJours] = useState(90);
  const [plateforme, setPlateforme] = useState('toutes');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    AdminService.getNewFamilies(jours, segment)
      .then((d) => { if (!annule) { setData(d); setError(null); } })
      .catch((err) => {
        console.error('Error fetching new families:', err);
        if (!annule) setError('Impossible de charger les nouvelles familles');
      })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [jours, segment]);

  const plateformes = data?.plateformes ?? [];
  const serie = serieFiltree(data?.serie ?? [], plateforme === 'toutes' ? null : plateforme);
  // Total de chaque plateforme sur la période, affiché dans son bouton de filtre.
  const totalPlateforme = (cle) => serieFiltree(data?.serie ?? [], cle)
    .reduce((s, r) => s + r.commencees + r.terminees + r.identifiees, 0);
  const totaux = ETAPES.reduce((acc, e) => ({ ...acc, [e.cle]: serie.reduce((s, r) => s + r[e.cle], 0) }), {});
  const total = ETAPES.reduce((s, e) => s + totaux[e.cle], 0);

  return (
    <Card>
      <CardHeader
        title="Nouvelles familles par jour"
        subheader="Une famille = le compte parent, ou le compte joueur seul s'il n'est rattaché à aucun parent ; datée au jour où son premier joueur a commencé l'onboarding, rangée selon l'étape la plus avancée atteinte depuis. Plateforme : celle du premier joueur, sinon celle du parent."
        avatar={<GroupAddIcon color="primary" />}
        action={
          <ToggleButtonGroup
            value={jours}
            exclusive
            size="small"
            onChange={(e, v) => { if (v) setJours(v); }}
            sx={{ pr: 1 }}
          >
            <ToggleButton value={30}>30 j</ToggleButton>
            <ToggleButton value={90}>90 j</ToggleButton>
            <ToggleButton value={180}>180 j</ToggleButton>
            <ToggleButton value={365}>1 an</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {plateformes.length > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Plateforme d'inscription :</Typography>
            <ToggleButtonGroup
              value={plateforme}
              exclusive
              size="small"
              onChange={(e, v) => { if (v) setPlateforme(v); }}
            >
              <ToggleButton value="toutes">Toutes</ToggleButton>
              {plateformes.map((cle) => (
                <ToggleButton key={cle} value={cle}>
                  {libellePlateforme(cle)} ({totalPlateforme(cle)})
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sur la période : <strong>{total}</strong> familles —{' '}
              {ETAPES.map((e, i) => (
                <span key={e.cle}>{i > 0 && ' · '}{e.libelle.toLowerCase()} : <strong>{totaux[e.cle]}</strong></span>
              ))}
              . Les derniers jours bougent encore : leurs familles peuvent finir l'onboarding ou s'identifier.
            </Typography>
            <Box sx={{ height: 380, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serie} barCategoryGap={1}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="jour"
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatJour}
                    angle={-45}
                    textAnchor="end"
                    height={50}
                    minTickGap={10}
                  />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip content={<Infobulle />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                  <Legend />
                  {ETAPES.map((e, i) => (
                    <Bar
                      key={e.cle}
                      dataKey={e.cle}
                      name={e.libelle}
                      stackId="familles"
                      fill={e.couleur}
                      stroke="#ffffff"
                      strokeWidth={1}
                      radius={i === ETAPES.length - 1 ? [3, 3, 0, 0] : 0}
                      shape={<Segment />}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
